
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCampaignName } from "@/hooks/campaign/hooks/useCampaignName";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

interface CampaignNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNameSubmit: (name: string) => void;
}

const formSchema = z.object({
  name: z.string().min(1, "Campaign name is required")
});

export const CampaignNameDialog = ({ open, onOpenChange, onNameSubmit }: CampaignNameDialogProps) => {
  const { t } = useLanguage();
  const { setCampaignName } = useCampaignName();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  });
  
  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({ name: "" });
    }
  }, [open, form]);

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    
    try {
      // Use the validated name from the form
      const finalName = values.name.trim();
      
      // Update campaign name in context
      setCampaignName(finalName);
      
      // Submit the name to parent component (this will handle the saving)
      onNameSubmit(finalName);
      
      // Close dialog
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting campaign name:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('nameCampaign')}</DialogTitle>
          <DialogDescription>
            {t('nameCampaignDescription')}
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="campaign-name">{t('campaignName')} *</Label>
                  <FormControl>
                    <Input 
                      id="campaign-name"
                      placeholder={t('enterCampaignName')}
                      {...field}
                      autoComplete="off"
                      className="col-span-3"
                      autoFocus
                      required
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-xs text-muted-foreground">
                    {t('campaignNameHelp')}
                  </p>
                </FormItem>
              )}
            />
            
            <DialogFooter className="mt-4">
              <Button 
                type="submit" 
                disabled={isSubmitting}
              >
                {isSubmitting ? t('saving') : t('startCampaign')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
